/**
 * HelpPanel — Side panel deslizable con descripción detallada del módulo.
 * Se abre al hacer clic en el botón de ayuda (?) junto al subtítulo de la página.
 * Se cierra con el botón X, clic en el backdrop, o tecla Escape.
 *
 * Uso:
 *   <PageHeader title="Plan de Cuentas" subtitle="..." help={HELP_CONTENT.accounts} />
 *
 * O standalone:
 *   <HelpPanel open={open} onClose={close} title="..." sections={[...]} />
 */
import { useState, useEffect } from 'react'
import { X, HelpCircle, Lightbulb, Target, CheckCircle2, Info, PlayCircle } from 'lucide-react'
import { useTour } from '@/lib/tours/TourContext'

export interface HelpSection {
  icon?: 'purpose' | 'how' | 'result' | 'tip' | 'info'
  title: string
  content: string
}

export interface HelpContent {
  title: string
  sections: HelpSection[]
}

interface HelpPanelProps {
  open: boolean
  onClose: () => void
  title: string
  sections: HelpSection[]
}

const SECTION_ICONS = {
  purpose: { icon: Target, cls: 'text-primary bg-primary-50' },
  how: { icon: Info, cls: 'text-info-content-soft bg-info-surface' },
  result: { icon: CheckCircle2, cls: 'text-success-content-soft bg-success-surface' },
  tip: { icon: Lightbulb, cls: 'text-warning-content-soft bg-warning-surface' },
  info: { icon: HelpCircle, cls: 'text-content-muted bg-surface-muted' },
}

/**
 * Renderiza contenido de HelpSection con formato básico:
 * - **bold** → <strong>
 * - Líneas que empiezan con `• ` o `- ` → lista con sangría
 * - Líneas que empiezan con `<dígitos>. ` → lista ordenada
 * - `\n\n` separa párrafos
 * - `\n` simple → salto de línea
 *
 * No usa innerHTML — todo se construye con React elements (seguro).
 */
function renderInline(text: string, keyPrefix: string): React.ReactNode[] {
  const parts: React.ReactNode[] = []
  const tokens = text.split(/(\*\*[^*]+\*\*)/g)
  tokens.forEach((tok, idx) => {
    if (!tok) return
    const m = tok.match(/^\*\*(.+)\*\*$/)
    if (m) {
      parts.push(<strong key={`${keyPrefix}-b${idx}`} className="text-content font-semibold">{m[1]}</strong>)
    } else {
      parts.push(<span key={`${keyPrefix}-t${idx}`}>{tok}</span>)
    }
  })
  return parts
}

function HelpContentBlock({ content }: { content: string }) {
  const paragraphs = content.split(/\n\n+/)
  return (
    <div className="text-sm text-content-muted leading-relaxed pl-[38px] space-y-2">
      {paragraphs.map((para, pi) => {
        const lines = para.split('\n')
        const bulletLines = lines.filter(l => /^\s*[•-]\s+/.test(l))
        const orderedLines = lines.filter(l => /^\s*\d+\.\s+/.test(l))

        if (bulletLines.length === lines.length && lines.length > 1) {
          return (
            <ul key={pi} className="space-y-1 list-none">
              {lines.map((l, li) => {
                const cleaned = l.replace(/^\s*[•-]\s+/, '')
                return (
                  <li key={li} className="flex gap-2">
                    <span className="text-content-subtle shrink-0">•</span>
                    <span>{renderInline(cleaned, `${pi}-${li}`)}</span>
                  </li>
                )
              })}
            </ul>
          )
        }

        if (orderedLines.length === lines.length && lines.length > 1) {
          return (
            <ol key={pi} className="space-y-1 list-none">
              {lines.map((l, li) => {
                const m = l.match(/^\s*(\d+)\.\s+(.*)/)
                return (
                  <li key={li} className="flex gap-2">
                    <span className="text-content-subtle shrink-0 font-medium">{m?.[1] || (li + 1)}.</span>
                    <span>{renderInline(m?.[2] || l, `${pi}-${li}`)}</span>
                  </li>
                )
              })}
            </ol>
          )
        }

        // Párrafo mixto: respeta \n simples como <br>
        return (
          <p key={pi}>
            {lines.map((l, li) => (
              <span key={li}>
                {renderInline(l, `${pi}-${li}`)}
                {li < lines.length - 1 && <br />}
              </span>
            ))}
          </p>
        )
      })}
    </div>
  )
}

export function HelpPanel({ open, onClose, title, sections }: HelpPanelProps) {
  const { availableTours, startTour } = useTour()

  useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [open, onClose])

  const handleStartTour = (id: Parameters<typeof startTour>[0]) => {
    onClose()
    // Pequeño delay para que se cierre el drawer antes de resaltar el elemento
    setTimeout(() => startTour(id), 250)
  }

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-40 bg-black/40 backdrop-blur-sm transition-opacity duration-300 ${open ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        onClick={onClose}
      />

      {/* Panel */}
      <div
        className={`fixed inset-y-0 right-0 z-50 w-full sm:w-96 bg-surface shadow-2xl border-l border-divider
          transform transition-transform duration-300 ease-out
          ${open ? 'translate-x-0' : 'translate-x-full'}`}
        style={{ backgroundColor: 'rgb(var(--color-surface))' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-divider">
          <div className="flex items-center gap-2">
            <HelpCircle size={18} className="text-primary" />
            <h2 className="text-base font-semibold text-content">{title}</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-soft text-content-subtle transition-colors"
          >
            <X size={16} />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto h-[calc(100%-57px)] px-5 py-5 space-y-5">
          {sections.map((section, i) => {
            const iconCfg = SECTION_ICONS[section.icon || 'info']
            const Icon = iconCfg.icon
            return (
              <div key={i}>
                <div className="flex items-center gap-2.5 mb-2">
                  <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${iconCfg.cls}`}>
                    <Icon size={14} />
                  </div>
                  <h3 className="text-sm font-semibold text-content">{section.title}</h3>
                </div>
                <HelpContentBlock content={section.content} />
              </div>
            )
          })}

          {/* Recorridos disponibles */}
          {availableTours.length > 0 && (
            <div className="pt-4 border-t border-divider">
              <div className="flex items-center gap-2.5 mb-3">
                <div className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0 bg-primary-50 text-primary">
                  <PlayCircle size={14} />
                </div>
                <h3 className="text-sm font-semibold text-content">Recorridos disponibles</h3>
              </div>
              <div className="space-y-2 pl-[38px]">
                {availableTours.map(tour => (
                  <button
                    key={tour.id}
                    onClick={() => handleStartTour(tour.id)}
                    className="w-full text-left p-3 rounded-lg border border-divider hover:border-primary hover:bg-primary-50/40 transition-colors group"
                  >
                    <p className="text-sm font-medium text-content flex items-center gap-1.5">
                      {tour.label}
                      <PlayCircle size={12} className="text-primary opacity-0 group-hover:opacity-100 transition-opacity" />
                    </p>
                    <p className="text-xs text-content-muted mt-0.5">{tour.description}</p>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  )
}

/**
 * PageHeader — Encabezado de página con título, subtítulo y botón de ayuda opcional.
 * Reemplaza el patrón repetido de <h1> + <p> en cada página.
 */
interface PageHeaderProps {
  title: string
  subtitle: string
  help?: HelpContent
  children?: React.ReactNode  // Acciones a la derecha (botones crear, etc.)
}

export function PageHeader({ title, subtitle, help, children }: PageHeaderProps) {
  const [open, setOpen] = useState(false)

  return (
    <>
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-semibold text-content">{title}</h1>
            {help && (
              <button
                onClick={() => setOpen(true)}
                data-tour="help-panel"
                className="p-1 rounded-full text-content-subtle hover:text-primary hover:bg-primary-50 transition-colors"
                title="Más información sobre este módulo"
                aria-label="Ayuda sobre este módulo"
              >
                <HelpCircle size={16} />
              </button>
            )}
          </div>
          <p className="text-sm text-content-muted mt-0.5">{subtitle}</p>
        </div>
        {children && <div className="flex items-center gap-2 shrink-0">{children}</div>}
      </div>

      {help && (
        <HelpPanel
          open={open}
          onClose={() => setOpen(false)}
          title={help.title}
          sections={help.sections}
        />
      )}
    </>
  )
}
